import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { COLLECTIONS } from '../platform/constants';
import { LAST_READ_MODULES } from '../platform/lastRead/lastReadModules';
import { markItemAsRead } from '../platform/lastRead/lastReadService';
import {
  isPinnedActive,
  parsePinnedUntil,
} from '../platform/pinnedContent';
import { withTenantScope } from './contentWriteFields';
import {
  applyTenantScopeToCollection,
  buildCreatedAtListQuery,
  mapAndFilterSnapshot,
} from './tenantListQuery';

export const POLLS_PAGE_SIZE = 20;
export const PINNED_POLLS_LIMIT = 5;

function pollsCollection() {
  return collection(db, COLLECTIONS.POLLS);
}

function buildPollsListQuery(user, {
  tenantId,
  memberships,
  pageSize = POLLS_PAGE_SIZE,
  lastVisible = null,
} = {}) {
  return buildCreatedAtListQuery({
    collectionName: COLLECTIONS.POLLS,
    tenantId,
    user,
    memberships,
    pageSize,
    lastVisible,
  });
}

export function buildPollsPageQuery(user, tenantId, memberships, options = {}) {
  return buildPollsListQuery(user, {
    tenantId,
    memberships,
    pageSize: options.pageSize ?? POLLS_PAGE_SIZE,
    lastVisible: options.startAfterDoc ?? null,
  });
}

export async function fetchPollsPage(user, tenantId, memberships, options = {}) {
  const listQuery = buildPollsPageQuery(user, tenantId, memberships, options);
  const querySnapshot = await getDocs(listQuery);
  return {
    polls: mapAndFilterSnapshot(querySnapshot, tenantId, mapPollDoc),
    lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
  };
}

export function mapPollDoc(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    anonymous: data.anonymous === true,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
    expires: data.expires?.toDate ? data.expires.toDate() : null,
    pinnedUntil: parsePinnedUntil(data.pinnedUntil),
    pinPriority: data.pinPriority ?? 0,
    unit: data.unit || 'general',
    unitName: data.unitName || 'Splošno',
  };
}

export function buildPinnedPollsQuery(tenantId) {
  const scoped = applyTenantScopeToCollection(COLLECTIONS.POLLS, tenantId);
  return query(
    scoped,
    where('pinnedUntil', '>', Timestamp.now()),
    orderBy('pinnedUntil', 'asc'),
    limit(PINNED_POLLS_LIMIT),
  );
}

export async function countActivePinnedPolls(tenantId, excludePollId = null) {
  if (!tenantId) {
    return 0;
  }

  const pinnedQuery = buildPinnedPollsQuery(tenantId);
  const querySnapshot = await getDocs(pinnedQuery);
  return querySnapshot.docs
    .map(mapPollDoc)
    .filter((poll) => isPinnedActive(poll))
    .filter((poll) => poll.id !== excludePollId)
    .length;
}

export function mapAndFilterPolls(querySnapshot, tenantId) {
  return mapAndFilterSnapshot(querySnapshot, tenantId, mapPollDoc);
}

function buildPollPinFields(pollData) {
  if (pollData.pinnedUntil) {
    return {
      pinnedUntil: pollData.pinnedUntil instanceof Date
        ? Timestamp.fromDate(pollData.pinnedUntil)
        : pollData.pinnedUntil,
      pinPriority: pollData.pinPriority ?? 0,
    };
  }
  return {
    pinnedUntil: null,
    pinPriority: 0,
  };
}

export async function createPoll(activeTenantId, pollData) {
  const docRef = await addDoc(
    pollsCollection(),
    withTenantScope(activeTenantId, {
      ...pollData,
      ...buildPollPinFields(pollData),
      createdAt: serverTimestamp(),
    }),
  );
  return docRef.id;
}

export async function updatePollContent(pollId, updates) {
  const {
    pinnedUntil,
    pinPriority,
    thumbnailUrl,
    ...rest
  } = updates;

  const payload = { ...rest };
  if (thumbnailUrl !== undefined) {
    payload.thumbnailUrl = thumbnailUrl;
  }
  if ('pinnedUntil' in updates) {
    Object.assign(payload, buildPollPinFields({ pinnedUntil, pinPriority }));
  }
  await updateDoc(doc(db, COLLECTIONS.POLLS, pollId), payload);
}

export async function markPollAsRead(userId, pollId) {
  if (!userId || !pollId) return;
  await markItemAsRead(userId, LAST_READ_MODULES.POLLS, pollId);
}

export async function fetchPollAnswers(pollId) {
  const answersSnapshot = await getDocs(collection(db, COLLECTIONS.POLLS, pollId, 'answers'));
  return answersSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
}

export async function hasUserVotedOnPoll(pollId, userId) {
  if (!pollId || !userId) return false;

  const answersQuery = query(
    collection(db, COLLECTIONS.POLLS, pollId, 'answers'),
    where('userId', '==', userId),
    limit(1),
  );
  const answersSnapshot = await getDocs(answersQuery);
  return !answersSnapshot.empty;
}

export async function loadUserVotedPollIds(pollIds, userId) {
  if (!userId || pollIds.length === 0) return {};

  const votedEntries = await Promise.all(
    pollIds.map(async (pollId) => {
      const voted = await hasUserVotedOnPoll(pollId, userId);
      return voted ? [pollId, true] : null;
    }),
  );

  return Object.fromEntries(votedEntries.filter(Boolean));
}
